import createDOMPurify, {type WindowLike} from "dompurify";

const classes = new Set(["font-modul", "highlight", "color", "text-color", "bold-accent"]);
const color = /^(?:#[\da-f]{3,8}|[a-z]+|(?:rgba?|hsla?|oklab|oklch|lab|lch|color)\([\da-z.%+,\s/-]+\))$/i;
const styles: Record<string, RegExp> = {
  color, "background-color": color, "--coverbaker-fragment-color": color,
  "font-weight": /^(?:normal|bold|[1-9]00)$/,
  "font-style": /^(?:normal|italic|oblique)$/,
  "text-decoration": /^(?:none|underline|line-through|overline)(?: (?:underline|line-through|overline))*$/,
};
const sanitizers = new WeakMap<object, ReturnType<typeof createDOMPurify>>();

/** One allowlist for native rich text, API documents and old saved scenes. */
export function sanitizeRichTextHtml(html: string, window: WindowLike): string {
  let purifier = sanitizers.get(window);
  if (!purifier) {
    purifier = createDOMPurify(window);
    purifier.addHook("uponSanitizeAttribute", (_node, attribute) => {
      if (attribute.attrName === "class") {
        attribute.attrValue = attribute.attrValue.split(/\s+/).filter((value) => classes.has(value)).join(" ");
        attribute.keepAttr = Boolean(attribute.attrValue);
      } else if (attribute.attrName === "style") {
        const declaration = window.document!.createElement("span").style;
        declaration.cssText = attribute.attrValue;
        const clean = window.document!.createElement("span").style;
        for (const [property, allowed] of Object.entries(styles)) {
          const value = declaration.getPropertyValue(property).trim();
          if (allowed.test(value)) clean.setProperty(property, value);
        }
        attribute.attrValue = clean.cssText;
        attribute.keepAttr = Boolean(attribute.attrValue);
      }
    });
    sanitizers.set(window, purifier);
  }
  return purifier.sanitize(html, {
    ALLOWED_TAGS: ["span", "b", "strong", "i", "em", "u", "s", "strike", "br", "div", "p", "ul", "ol", "li", "sub", "sup"],
    ALLOWED_ATTR: ["class", "style", "data-content-editable-tail"],
    ALLOW_DATA_ATTR: false, ALLOW_ARIA_ATTR: false,
  });
}
